"use client";

import { useProducts } from "@/hooks/useProduct";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Locales } from "@/lib/i18n";
import { cn, extractKeyTextValue, isEnrichedProduct } from "@/lib/utils";
import { CategorieDocument, ProductDocument } from "@/prismicio-types";
import Link from "next/link";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { renderProductCard } from "@/components/molecules/render-product-card";
import { __ } from "@/lib/i18n/functions";
import { EnrichedProduct } from "@/types";

type BestSellersProps = {
  headline: string;
  description: string;
  products: (ProductDocument | EnrichedProduct)[] | null;
  productCategories: CategorieDocument[][] | null;
  className?: string;
  variant?: "default" | "payment";
  link?: string;
  linkText?: string;
  locale: Locales;
};

const BestSellers = ({
  headline,
  description,
  className,
  variant = "default",
  link,
  linkText,
  locale,
  products,
  productCategories,
}: BestSellersProps) => {
  const medusaIds =
    (products &&
      products?.map((product) =>
        isEnrichedProduct(product)
          ? extractKeyTextValue(product.prismic.medusaId)
          : extractKeyTextValue(product.data.medusaId)
      )) ??
    [];
  const { data: medusaProducts, isLoading } = useProducts(locale, medusaIds, medusaIds.length > 0);
  if (products?.length === 0) return null;

  return (
    <section
      className={cn("space-y-7.5 py-6 max-sm:px-4 md:py-12", className)}
      aria-labelledby={__("Best Selling Products", locale)}
    >
      <header
        className={cn(
          "container flex flex-col items-center justify-center gap-2.5 px-4 md:px-7.5",
          variant === "payment" && "flex-row flex-wrap gap-3 sm:justify-between"
        )}
      >
        {headline && (
          <h2 className="text-blue-dark font-cabinet-grotesk text-center text-3xl font-medium">{headline}</h2>
        )}
        {description && (
          <p className={cn("text-primary-light text-center text-lg", variant === "payment" && "sm:hidden")}>
            {description}
          </p>
        )}
        {linkText && (
          <Link
            className={cn(
              "text-dark-primary focus-visible:ring-dark-primary/70 rounded-sm text-base font-medium underline focus-visible:ring-[3px] focus-visible:ring-offset-3 focus-visible:outline-none max-sm:hidden md:uppercase",
              variant === "default" && "hidden"
            )}
            href={link || "#"}
          >
            {linkText}
          </Link>
        )}
      </header>

      <Carousel className="container flex flex-col-reverse gap-4.5 sm:px-7.5">
        <CarouselContent className="-ml-4 p-1 sm:-ml-5" role="list">
          {products?.map((product, index) => {
            const medusaId = isEnrichedProduct(product)
              ? extractKeyTextValue(product.prismic.medusaId)
              : extractKeyTextValue(product.data.medusaId);
            const medusaProduct = medusaProducts?.[medusaId];
            const category = productCategories?.[index]?.[0];

            return (
              <CarouselItem
                key={index}
                className={cn(
                  "basis-full pl-4 sm:basis-1/2 sm:pl-5 lg:basis-1/3",
                  products.length >= 4 && "xl:basis-1/4",
                  products.length === 2 && "xl:basis-1/2",
                  products.length === 1 && "xl:basis-1/4"
                )}
              >
                {renderProductCard({
                  product,
                  medusaProduct,
                  category,
                  locale,
                  isLoading,
                  className: cn("max-w-full md:max-w-none", products.length >= 4 && "xl:max-w-none"),
                })}
              </CarouselItem>
            );
          })}
        </CarouselContent>

        <div className={cn("flex items-center justify-center gap-7", products && products.length <= 4 && "xl:hidden")}>
          <CarouselPrevious
            variant="light"
            className="sm:bg-pink-primary sm:hover:pink-overlay size-11 rounded-none bg-white text-black disabled:opacity-50 sm:absolute sm:top-[37%] sm:left-0 sm:text-white"
            aria-label={__("Go to previous product", locale)}
          >
            <ArrowLeft size={20} />
          </CarouselPrevious>
          <CarouselNext
            variant="light"
            className="sm:bg-pink-primary sm:hover:pink-overlay size-11 rounded-none bg-white text-black disabled:opacity-50 sm:absolute sm:top-[37%] sm:right-0 sm:text-white"
            aria-label={__("Go to next product", locale)}
          >
            <ArrowRight size={20} />
          </CarouselNext>
        </div>
      </Carousel>
    </section>
  );
};

export default BestSellers;
